import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@radix-ui/react-popover';
import moment from 'moment';
import { useForm } from 'react-hook-form';

import { AgreementDTO, CommentDTO } from '@/types/api-types';

import IconAddTag from '@/assets/svg/icon-add-tag.svg?react';
import IconClose from '@/assets/svg/icon-close.svg?react';
import IconFolder from '@/assets/svg/icon-folder.svg?react';
import IconEdit from '@/assets/svg/icon-pencil.svg?react';
import IconDelete from '@/assets/svg/icon-trash.svg?react';

import { cn } from '@/lib/utils';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { UserSelect } from '@/components/ui/user-select';

import { useApiManagerStore } from '@/stores/apiManagerStore';
import { useAuthStore } from '@/stores/authStore';
import { useFileExplorerStore } from '@/stores/fileExplorerStore';
import { useGlobalStore } from '@/stores/globalStore';

import { apiService } from '@/services/api-service';

import {
  END_STEP_NAME,
  START_STEP_NAME,
  USER_ROLES,
} from '@/constants/global-constants';

import { ConfirmationDialog } from '../ConfirmationDialog/ConfirmationDialog';

type CommentFilterMode = 'comments' | 'logs' | 'all';

interface DetailsPanelProps {
  isOpen: boolean;
  directoryAbbreviation: string;
  onClose: () => void;
  data: AgreementDTO;
  onEdit?: (data: AgreementDTO) => void;
}

export const DetailsPanel: FC<DetailsPanelProps> = ({
  isOpen,
  directoryAbbreviation,
  onClose,
  data,
  onEdit,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isCommentsLoading, setIsCommentsLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [comments, setComments] = useState<CommentDTO[]>([]);
  const [loadedCommentsCount, setLoadedCommentsCount] = useState(0);
  const [hasMoreComments, setHasMoreComments] = useState(false);
  const COMMENTS_BATCH_SIZE = 20;

  const [isAddingComment, setIsAddingComment] = useState(false);
  const [deleteCommentId, setDeleteCommentId] = useState<string>('');
  const [commentFilter, setCommentFilter] =
    useState<CommentFilterMode>('comments');
  const { setAgreements, agreements } = useApiManagerStore();
  const { updateAgreementInStateByDTO } = useFileExplorerStore();

  const { steps: rawSteps, users: rawUsers, allTags, getAutoFetchUsers } = useGlobalStore();
  const steps = rawSteps ?? [];
  const users = rawUsers ?? [];

  const { user, getCurrentUser } = useAuthStore();

  const sortedSteps = steps
    .filter(
      (s) =>
        s.user_type === getCurrentUser()?.role &&
        data.process !== null &&
        s.process_set.includes(data.process),
    )
    .sort((a, b) => {
      if (a.status_name === START_STEP_NAME) {
        return -1;
      }

      if (b.status_name === START_STEP_NAME) {
        return 1;
      }

      if (a.status_name === END_STEP_NAME) {
        return 1;
      }

      if (b.status_name === END_STEP_NAME) {
        return -1;
      }

      return 0;
    });

  const [newComment, setNewComment] = useState<{
    content: string;
    uuid?: string;
  }>({
    content: '',
  });

  const form = useForm<AgreementDTO>({
    defaultValues: {
      tag_set: [],
    },
    mode: 'onSubmit',
  });

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleSave = async (values: AgreementDTO) => {
    if (!isEditing) return;
    try {
      let newValuesAgreement = { ...values };
      if (data.step !== values.step) {
        const newComment = await apiService.createComment({
          content: `Статус змінено з ${
            steps.find((s) => s.uuid === data.step)?.status_name
          } на ${steps.find((s) => s.uuid === values.step)?.status_name}`,
          agreement: data.uuid,
          user: getCurrentUser()?.uuid!,
          is_edited: false,
          created: new Date().toISOString(),
          modified: new Date().toISOString(),
          is_system_comment: true,
        });
        const agreementId = data.uuid;
        const foundAgreement = agreements.find((a) => a.uuid === agreementId);

        if (foundAgreement) {
          setAgreements(
            agreements.map((a) =>
              a.uuid === agreementId
                ? {
                    ...a,
                    comments: [newComment.uuid, ...(a.comments || [])],
                  }
                : a,
            ),
          );
          updateAgreementInStateByDTO({
            ...foundAgreement,
            comments: [newComment.uuid, ...(foundAgreement.comments || [])],
          });
        }

        setComments((prev) => [newComment, ...prev]);
        newValuesAgreement = {
          ...values,
          comments: values.comments
            ? [...values.comments, newComment.uuid]
            : [newComment.uuid],
        };
      }
      await onEdit?.(newValuesAgreement);

      form.reset({
        ...newValuesAgreement,
      });
      setIsEditing(false);
    } catch (error) {}
  };

  const handleCancel = () => {
    setIsEditing(false);
    form.reset();
  };

  const getUserName = useMemo(
    () => (uuid: string) => {
      const author = users.find((u) => u.uuid === uuid);
      return author ? `${author.first_name} ${author.last_name}` : '';
    },
    [users],
  );

  // Filter comments based on selected mode
  const filteredComments = useMemo(() => {
    switch (commentFilter) {
      case 'comments':
        return comments.filter((c) => !c.is_system_comment);
      case 'logs':
        return comments.filter((c) => c.is_system_comment);
      case 'all':
      default:
        return comments;
    }
  }, [comments, commentFilter]);

  const handleAddComment = async () => {
    if (!data || !newComment.content.trim()) return;

    try {
      const commentToUpdate = {
        content: newComment.content,
        agreement: data.uuid,
        user: user?.uuid!,
        is_edited: false,
        created: new Date().toISOString(),
        modified: new Date().toISOString(),
        is_system_comment: false,
      };
      const comment = newComment.uuid
        ? await apiService.updateComment(newComment.uuid, {
            ...commentToUpdate,
            uuid: newComment.uuid,
          })
        : await apiService.createComment(commentToUpdate);

      // Keep agreements store in sync so comments persist across mount/unmount
      const agreementId = data.uuid;
      const foundAgreement = agreements.find((a) => a.uuid === agreementId);
      if (foundAgreement) {
        setAgreements([
          ...agreements.filter((a) => a.uuid !== agreementId),
          {
            ...foundAgreement,
            comments: [comment.uuid, ...(foundAgreement.comments || [])],
          },
        ]);
        updateAgreementInStateByDTO({
          ...foundAgreement,
          comments: [comment.uuid, ...(foundAgreement.comments || [])],
        });
      }

      const newCommentsUpdated = newComment.uuid
        ? comments.map((c) => (c.uuid === newComment.uuid ? comment : c))
        : [comment, ...comments];

      setComments(
        newCommentsUpdated.sort(
          (a, b) =>
            new Date(b.modified).getTime() - new Date(a.modified).getTime(),
        ),
      );
      setNewComment({ content: '', uuid: '' });
      setIsAddingComment(false);
    } catch (error) {}
  };

  const handleEditComment = async (comment: CommentDTO) => {
    setNewComment({ content: comment.content, uuid: comment.uuid });
    setIsAddingComment(true);
  };

  const handleDeleteComment = async (commentId: string) => {
    try {
      await apiService.deleteComment(commentId);
      setComments(comments.filter((c) => c.uuid !== commentId));
      setDeleteCommentId('');
      // Remove deleted comment from agreements store to prevent it from reappearing on remount
      const agreementId = data.uuid;
      const foundAgreement = agreements.find((a) => a.uuid === agreementId);
      if (foundAgreement) {
        setAgreements([
          ...agreements.filter((a) => a.uuid !== agreementId),
          {
            ...foundAgreement,
            comments: (foundAgreement.comments || []).filter(
              (id) => id !== commentId,
            ),
          },
        ]);
        updateAgreementInStateByDTO({
          ...foundAgreement,
          comments: (foundAgreement.comments || []).filter(
            (id) => id !== commentId,
          ),
        });
      }
    } catch (error) {}
  };

  const loadMoreComments = async () => {
    if (!data) return;

    const commentsUuids =
      (getCommentsUuids(data) as string[] | undefined) || [];

    const start = loadedCommentsCount;
    const end = start + COMMENTS_BATCH_SIZE;
    const commentIds = commentsUuids.slice(start, end);

    if (commentIds.length > 0) {
      setIsCommentsLoading(true);
      try {
        const newComments = await apiService.getComments(commentIds);
        setComments((prevComments) => [...prevComments, ...newComments]);
        setLoadedCommentsCount((prevCount) => prevCount + commentIds.length);
        setHasMoreComments(end < commentsUuids.length);
      } catch (error) {
        console.error('Failed to load more comments', error);
      } finally {
        setIsCommentsLoading(false);
      }
    }
  };

  const getCommentsUuids = useCallback(
    (data: AgreementDTO) => {
      const agreementUuid = data.uuid;
      const commentsUuids = agreements.find(
        (a) => a.uuid === agreementUuid,
      )?.comments;
      return commentsUuids;
    },
    [agreements],
  );

  useEffect(() => {
    async function fetchInitialComments(data: AgreementDTO) {
      try {
        setIsCommentsLoading(true);
        const commentsUuids = getCommentsUuids(data);

        if (commentsUuids && commentsUuids.length > 0) {
          const initialCommentIds = commentsUuids.slice(0, COMMENTS_BATCH_SIZE);
          const commentsResponse =
            await apiService.getComments(initialCommentIds);

          await getAutoFetchUsers(commentsResponse.map((c) => c.user));

          setComments(
            commentsResponse
              .filter((comment) => comment.agreement === data.uuid)
              .sort(
                (a, b) =>
                  new Date(b.modified).getTime() -
                  new Date(a.modified).getTime(),
              ),
          );

          setLoadedCommentsCount(initialCommentIds.length);
          setHasMoreComments(commentsUuids.length > COMMENTS_BATCH_SIZE);
        }

        form.reset({
          ...data,
        });

        setIsLoading(false);
        setIsCommentsLoading(false);
      } catch (error) {
        setIsLoading(false);
        setIsCommentsLoading(false);
      }
    }

    if (data) {
      fetchInitialComments(data);
    }
  }, [data]);

  useEffect(() => {
    return () => {
      onClose();
    };
  }, []);

  return (
    <div
      className={`absolute right-[-50px] top-0 w-full transform rounded-md bg-white shadow-sm transition-transform duration-300 ease-in-out sm:w-[524px] ${
        isOpen ? 'translate-x-[-50px]' : 'translate-x-full'
      } z-50 h-full`}
    >
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSave)} className="h-full">
          <div className="flex h-full flex-col">
            <div className="flex flex-col gap-4 border-b py-6 pl-9 pr-5">
              <div className="flex items-center justify-between">
                <div>
                  {data ? (
                    <h2 className="flex items-center gap-4 text-xl font-medium">
                      <IconFolder className="h-[14px] w-[18px]" />
                      {`${directoryAbbreviation} > ${data.name}`}
                    </h2>
                  ) : (
                    <Skeleton className="h-7 w-48" />
                  )}
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={onClose}
                  className="p-1.5"
                >
                  <IconClose className="h-4 w-4" />
                </Button>
              </div>
              <span className="text-base font-semibold">
                {isAddingComment
                  ? newComment.uuid
                    ? 'Змінити коментар'
                    : 'Новий коментар'
                  : 'Деталі документу'}
              </span>
            </div>

            {/* Content */}
            <div className="custom-scrollbar flex flex-1 flex-col space-y-[18px] overflow-y-auto px-9 pb-6 pt-4">
              {!isLoading && data ? (
                isAddingComment ? (
                  <div className="flex flex-col gap-4">
                    <Textarea
                      value={newComment.content}
                      onChange={(e) =>
                        setNewComment({
                          ...newComment,
                          content: e.target.value,
                        })
                      }
                      placeholder="Введіть ваш коментар..."
                      className="min-h-[450px]"
                    />
                    <div className="flex justify-between gap-2">
                      <Button
                        variant="outline"
                        className="h-[40px] w-[180px]"
                        type="button"
                        onClick={handleAddComment}
                        disabled={!newComment.content.trim()}
                        size="sm"
                      >
                        {newComment.uuid
                          ? 'Змінити коментар'
                          : 'Додати коментар'}
                      </Button>
                      <Button
                        variant="destructive"
                        className="h-[40px] w-[180px]"
                        type="button"
                        onClick={() => setIsAddingComment(false)}
                        size="sm"
                      >
                        Скасувати
                      </Button>
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Responsible */}
                    <FormField
                      control={form.control}
                      name="assignee"
                      render={({ field }) => (
                        <FormItem className="space-y-0">
                          <FormLabel className="text-base font-medium text-gray-200">
                            Відповідальний
                          </FormLabel>
                          <div className="flex items-center">
                            {isEditing &&
                            getCurrentUser()?.role !==
                              USER_ROLES.EXECUTOR.value ? (
                              <UserSelect
                                value={field.value}
                                onValueChange={field.onChange}
                                usersUuidsFromAgreement={[
                                  ...(data.assignee ? [data.assignee] : []),
                                  data.creator,
                                ]}
                              >
                                <FormControl>
                                  <SelectTrigger className="relative w-full">
                                    <SelectValue
                                      placeholder={
                                        isLoading
                                          ? 'Завантаження користувачів...'
                                          : 'Виберіть користувача...'
                                      }
                                    />
                                    {isLoading && (
                                      <div className="absolute left-4 top-1/2 -translate-y-1/2 transform">
                                        <div className="h-4 w-4 animate-spin-slow rounded-full border-2 border-transparent border-l-primary border-t-primary"></div>
                                      </div>
                                    )}
                                  </SelectTrigger>
                                </FormControl>
                              </UserSelect>
                            ) : (
                              <span className="">
                                {data.assignee
                                  ? getUserName(data.assignee)
                                  : ''}
                              </span>
                            )}
                          </div>
                        </FormItem>
                      )}
                    />

                    {/* Status */}
                    <FormField
                      control={form.control}
                      name="step"
                      render={({ field }) => (
                        <FormItem className="space-y-0">
                          <FormLabel className="text-base font-medium text-gray-200">
                            Статус
                          </FormLabel>
                          <div className="flex items-center">
                            {isEditing ? (
                              <Select
                                onValueChange={field.onChange}
                                value={field.value}
                              >
                                <FormControl>
                                  <SelectTrigger className="w-full">
                                    <SelectValue />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  {sortedSteps.map((step) => (
                                    <SelectItem
                                      key={step.uuid}
                                      value={step.uuid}
                                    >
                                      {step.status_name}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            ) : (
                              <span className="">
                                {
                                  steps.find((step) => step.uuid === data.step)
                                    ?.status_name
                                }
                              </span>
                            )}
                          </div>
                        </FormItem>
                      )}
                    />

                    {/* Tags */}
                    <FormField
                      control={form.control}
                      name="tag_set"
                      render={({ field }) => (
                        <FormItem className="space-y-0">
                          <FormLabel className="text-base font-medium text-gray-200">
                            Мітки
                          </FormLabel>
                          <div className="flex flex-wrap items-center gap-2">
                            {field.value.map((tag) => (
                              <Badge
                                key={tag}
                                variant="outline"
                                className="h-6 gap-1 rounded border-separator bg-white px-1 py-1 text-sm font-normal text-primary"
                              >
                                {allTags.entities[tag]?.name}
                                {isEditing && (
                                  <button
                                    className="relative z-10 rounded-sm hover:bg-blue-500 hover:[&>svg]:fill-white"
                                    onClick={() =>
                                      field.onChange(
                                        field.value.filter((t) => t !== tag),
                                      )
                                    }
                                  >
                                    <IconClose className="z-0 min-h-3 min-w-3 fill-blue-500 p-0.5 transition-colors" />
                                  </button>
                                )}
                              </Badge>
                            ))}
                            {isEditing && (
                              <Popover>
                                <PopoverTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-6 w-6 p-0"
                                    type="button"
                                  >
                                    <IconAddTag className="h-4 w-4" />
                                  </Button>
                                </PopoverTrigger>
                                <PopoverContent>
                                  <div className="flex flex-col overflow-hidden rounded-md border-primary bg-white shadow-sm">
                                    {allTags.rootIds
                                      .filter(
                                        (tag) =>
                                          !field.value.some((t) => t === tag),
                                      )
                                      .map((tag) => (
                                        <div
                                          key={tag}
                                          className="cursor-pointer border-b border-separator px-4 py-3 transition-colors last:border-b-0 hover:bg-blue-200"
                                          onClick={() =>
                                            field.onChange([
                                              ...field.value,
                                              tag,
                                            ])
                                          }
                                        >
                                          {allTags.entities[tag]?.name}
                                        </div>
                                      ))}
                                  </div>
                                </PopoverContent>
                              </Popover>
                            )}
                          </div>
                        </FormItem>
                      )}
                    />

                    {/* Metadata */}
                    <div className="flex flex-col">
                      <span className="text-base font-medium text-gray-200">
                        Автор
                      </span>
                      <span className="">
                        {data.creator ? getUserName(data.creator) : ''}
                      </span>
                    </div>
                    <div className="flex flex-col">
                      <span className="text-base font-medium text-gray-200">
                        Створено
                      </span>
                      <span className="">
                        {moment(data.created).format('DD.MM.YYYY HH:mm')}
                      </span>
                    </div>
                    <div className="flex items-end justify-between">
                      <div className="flex flex-col">
                        <span className="text-base font-medium text-gray-200">
                          Змінено
                        </span>
                        <span className="">
                          {moment(data.modified).format('DD.MM.YYYY HH:mm')}
                        </span>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        type="button"
                        onClick={() => setIsAddingComment(true)}
                      >
                        Додати коментар
                      </Button>
                    </div>
                    <div className="relative flex h-full max-h-full min-h-[200px] flex-col overflow-hidden rounded-md border border-gray-200">
                      {/* Comment filter toggle - inside the box */}
                      <div className="z-10 flex items-center justify-center border-b border-gray-200 bg-separator">
                        <div className="flex flex-1 items-center">
                          <button
                            onClick={() => setCommentFilter('comments')}
                            className={cn(
                              'flex-1 px-5 py-3 text-sm transition-colors',
                              commentFilter === 'comments'
                                ? 'rounded-t-md rounded-br-md bg-white text-primary'
                                : 'hover:text-primary',
                            )}
                          >
                            Коментарі
                          </button>
                          <button
                            onClick={() => setCommentFilter('logs')}
                            className={cn(
                              'flex-1 px-5 py-3 text-sm transition-colors',
                              commentFilter === 'logs'
                                ? 'rounded-md bg-white text-primary'
                                : 'hover:text-primary',
                            )}
                          >
                            Технічні логи
                          </button>
                          <button
                            onClick={() => setCommentFilter('all')}
                            className={cn(
                              'flex-1 px-5 py-3 text-sm transition-colors',
                              commentFilter === 'all'
                                ? 'rounded-t-md rounded-bl-md bg-white text-primary'
                                : 'hover:text-primary',
                            )}
                          >
                            Показати усе
                          </button>
                        </div>
                      </div>
                      {/* Scrollable content area */}
                      <div className="custom-scrollbar flex-1 overflow-y-auto">
                        {isCommentsLoading && comments.length === 0 ? (
                          <div className="flex h-full items-center justify-center p-4">
                            <div className="flex flex-col items-center gap-2">
                              <div className="animate-spin h-6 w-6 rounded-full border-2 border-transparent border-l-primary border-t-primary"></div>
                              <span className="text-sm text-gray-400">
                                Завантаження коментарів...
                              </span>
                            </div>
                          </div>
                        ) : (
                          <>
                            {filteredComments.map((comment) => (
                              <div
                                key={comment.uuid}
                                className="flex flex-col gap-2 border-b border-separator p-2.5"
                              >
                                <div className="flex items-center justify-between">
                                  <span className="text-sm font-medium text-gray-200">
                                    {getUserName(comment.user)}{' '}
                                    {moment(
                                      comment.is_edited
                                        ? comment.modified
                                        : comment.created,
                                    ).format('DD.MM.YYYY HH:mm')}
                                  </span>
                                  {comment.user === getCurrentUser()?.uuid &&
                                    !comment.is_system_comment && (
                                      <div className="flex items-end gap-2">
                                        <button
                                          onClick={() =>
                                            handleEditComment(comment)
                                          }
                                          className="mr-1"
                                        >
                                          <IconEdit className="h-3.5 w-3.5" />
                                        </button>
                                        <button
                                          onClick={() =>
                                            setDeleteCommentId(comment.uuid)
                                          }
                                        >
                                          <IconDelete className="h-4 w-4 fill-destructive" />
                                        </button>
                                      </div>
                                    )}
                                </div>
                                <span className="break-words">
                                  {comment.content}
                                </span>
                              </div>
                            ))}
                            {hasMoreComments && filteredComments.length > 0 && (
                              <div className="flex justify-center p-2.5">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={loadMoreComments}
                                  className="hover:text-primary/80 text-primary"
                                  disabled={isCommentsLoading}
                                >
                                  {isCommentsLoading ? (
                                    <div className="flex items-center gap-2">
                                      <div className="h-4 w-4 animate-spin-slow rounded-full border-2 border-transparent border-l-primary border-t-primary"></div>
                                      <span>Завантаження...</span>
                                    </div>
                                  ) : (
                                    'Завантажити ще'
                                  )}
                                </Button>
                              </div>
                            )}
                            {filteredComments.length === 0 &&
                              !isCommentsLoading && (
                                <div className="flex h-full items-center justify-center p-4">
                                  <span className="text-sm text-gray-400">
                                    {commentFilter === 'comments' &&
                                      'Немає коментарів'}
                                    {commentFilter === 'logs' &&
                                      'Немає технічних логів'}
                                    {commentFilter === 'all' &&
                                      'Немає коментарів'}
                                  </span>
                                </div>
                              )}
                          </>
                        )}
                      </div>
                    </div>
                  </>
                )
              ) : (
                <>
                  <div className="space-y-3">
                    <Skeleton className="h-6 w-32" />
                    <Skeleton className="h-10 w-full" />
                  </div>
                  <div className="space-y-3">
                    <Skeleton className="h-6 w-24" />
                    <Skeleton className="h-10 w-full" />
                  </div>
                  <div className="space-y-3">
                    <Skeleton className="h-6 w-20" />
                    <div className="flex gap-2">
                      <Skeleton className="h-6 w-20" />
                      <Skeleton className="h-6 w-20" />
                      <Skeleton className="h-6 w-20" />
                    </div>
                  </div>
                  <div className="space-y-3">
                    <Skeleton className="h-6 w-24" />
                    <Skeleton className="h-4 w-48" />
                  </div>
                  <div className="space-y-3">
                    <Skeleton className="h-6 w-24" />
                    <Skeleton className="h-4 w-36" />
                  </div>
                  <div className="space-y-3">
                    <Skeleton className="h-6 w-24" />
                    <Skeleton className="h-4 w-36" />
                  </div>
                  <div className="flex flex-col gap-3 rounded-md border p-4">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="space-y-2">
                        <Skeleton className="h-4 w-48" />
                        <Skeleton className="h-3 w-full" />
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Footer Actions - Only show when not adding comment */}
            {!isAddingComment && (
              <div className="flex justify-between space-x-4 border-t px-9 py-6">
                {!isLoading && data ? (
                  isEditing ? (
                    <>
                      <Button
                        variant="destructive"
                        type="submit"
                        disabled={form.formState.isSubmitting}
                      >
                        {form.formState.isSubmitting
                          ? 'Збереження...'
                          : 'Зберегти'}
                      </Button>
                      <Button
                        variant="outline"
                        onClick={handleCancel}
                        type="button"
                        disabled={form.formState.isSubmitting}
                      >
                        Скасувати
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button
                        key={'edit'}
                        variant="outline"
                        type="button"
                        onClick={handleEditClick}
                      >
                        Редагувати
                      </Button>
                      <Button variant="destructive" type="button" disabled>
                        Зберегти
                      </Button>
                    </>
                  )
                ) : (
                  <>
                    <Skeleton className="h-10 w-32" />
                    <Skeleton className="h-10 w-32" />
                  </>
                )}
              </div>
            )}
          </div>
        </form>
      </Form>
      <ConfirmationDialog
        isOpen={!!deleteCommentId}
        onClose={() => setDeleteCommentId('')}
        onConfirm={() => handleDeleteComment(deleteCommentId)}
        title="Ви впевнені що хочете видалити коментар?"
        confirmText="Видалити"
        cancelText="Скасувати"
        variant="destructive"
      />
    </div>
  );
};
